/**
 * Marketing hero copy block (eyebrow, title, body) for landing hero sections.
 * Layout/alignment only; parent handles scroll or section positioning.
 */

import React from 'react';
import {
  getColorStop,
  type PlayerColorFamily,
} from '../../styles/playerColors';
import { cn } from '../../utilities/cn';

const joinClassNames = (...classNames: Array<string | undefined>): string =>
  classNames.filter(Boolean).join(' ');

type HeroTextPanelPalette = 'neutral' | PlayerColorFamily;

export interface HeroTextPanelProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  body?: React.ReactNode;
  /** When set with `body`, renders body as an external link (attribution). */
  bodyHref?: string;
  /** Wraps the title in a blockquote (e.g. testimonial). */
  testimonial?: boolean;
  palette?: HeroTextPanelPalette;
  /** Merged onto the root; override the default left alignment here
   *  (e.g. `items-center text-center`). */
  className?: string;
}

/**
 * Hero title sizing.
 *
 * Width ladder grows from `text-4xl` (mobile) through `text-8xl` (xl) and
 * caps there for the standard 2xl tier (1080p-class displays). Past that,
 * we add intentionally restrained growth for ultra-wide tiers so QHD/4K
 * viewers don't see a tiny title floating in a huge viewport, but the title
 * never balloons large enough to make a multi-line hero overflow common
 * viewport heights (1080, 1440).
 *
 * Height ladder shrinks the title on short viewports (1366×768, 1280×720,
 * and laptops at 110–125% DPI zoom) and on very narrow + short combos like
 * iPhone SE so it doesn't overflow the area between the fixed header and
 * the body copy. The height ranges are exclusive, since Tailwind 4 orders
 * arbitrary media variants by its own rules, not the class string's.
 *
 * Width-cap ladder (`max-w-*`) follows the same growth so line-lengths stay
 * sensible at the largest sizes.
 */
const TITLE_CLASS_BASE = [
  'font-heading text-foreground',
  // Width ladder (deliberately holds at text-8xl through 2xl; bigger growth
  // unlocked only on truly large displays).
  'text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl',
  'min-[1920px]:text-9xl',
  'min-[2560px]:text-[10rem]',
  'min-[3840px]:text-[12rem]',
  // Max-width ladder
  'max-w-5xl 2xl:max-w-6xl min-[1920px]:max-w-[80rem] min-[2560px]:max-w-[96rem]',
  // Short-viewport caps. NOTE: Tailwind v4 does NOT preserve source order
  // for arbitrary media-query variants — it sorts them by its own
  // algorithm. So overlapping rules (e.g. one bare `max-h-720` and another
  // bare `max-h-820`) produce unpredictable cascade winners.
  //
  // We therefore use mutually-exclusive height × width ranges below: every
  // viewport matches AT MOST ONE of these rules. The standard width-based
  // ladder above handles every other viewport.
  // — Short (≤720px tall):
  '[@media(max-height:720px)_and_(max-width:479px)]:text-xl',
  '[@media(max-height:720px)_and_(min-width:480px)_and_(max-width:1023px)]:text-4xl',
  '[@media(max-height:720px)_and_(min-width:1024px)]:text-5xl',
  // — Medium-short (721–820px tall):
  '[@media(min-height:721px)_and_(max-height:820px)_and_(max-width:479px)]:text-3xl',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:480px)_and_(max-width:1023px)]:text-5xl',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:text-6xl',
].join(' ');

const HeroTextPanel: React.FC<HeroTextPanelProps> = ({
  eyebrow,
  title,
  body,
  bodyHref,
  testimonial = false,
  palette = 'neutral',
  className,
}) => {
  const accentColor =
    palette === 'neutral' ? undefined : getColorStop(palette, '700');
  const titleColor =
    palette === 'neutral' ? undefined : getColorStop(palette, '900');

  const titleClassName = joinClassNames(
    TITLE_CLASS_BASE,
    testimonial ? 'leading-tight' : 'leading-none'
  );

  const titleStyle = titleColor ? { color: titleColor } : undefined;

  const bodyParagraphClassName = [
    'max-w-4xl font-normal leading-relaxed text-muted-foreground',
    'text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl',
    '2xl:text-4xl min-[1920px]:text-[2.5rem] min-[2560px]:text-[3rem] min-[3840px]:text-[3.5rem]',
    '2xl:max-w-5xl min-[1920px]:max-w-6xl',
    // Mutually exclusive short-viewport caps (see title comment above).
    '[@media(max-height:720px)_and_(max-width:479px)]:text-xs',
    '[@media(max-height:720px)_and_(min-width:480px)_and_(max-width:1023px)]:text-sm',
    '[@media(max-height:720px)_and_(min-width:1024px)]:text-base',
    '[@media(min-height:721px)_and_(max-height:820px)_and_(max-width:479px)]:text-sm',
    '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:480px)_and_(max-width:1023px)]:text-base',
    '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:text-lg',
  ].join(' ');

  const bodyLinkClassName = [
    'max-w-4xl font-medium uppercase tracking-widest underline-offset-4 hover:underline',
    'text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl',
    '2xl:text-3xl min-[1920px]:text-4xl min-[2560px]:text-[2.75rem]',
    // Mutually exclusive short-viewport caps.
    '[@media(max-height:720px)_and_(max-width:479px)]:text-xs',
    '[@media(max-height:720px)_and_(min-width:480px)]:text-xs',
    '[@media(min-height:721px)_and_(max-height:820px)]:text-sm',
  ].join(' ');

  const bodyColorStyle = accentColor ? { color: accentColor } : undefined;

  const wrapperGapClassName = [
    'flex flex-col',
    'gap-4 sm:gap-5 md:gap-6 lg:gap-8 xl:gap-10',
    '2xl:gap-10 min-[1920px]:gap-12 min-[2560px]:gap-14',
    // Mutually exclusive short-viewport caps.
    '[@media(max-height:720px)_and_(max-width:479px)]:gap-1',
    '[@media(max-height:720px)_and_(min-width:480px)]:gap-2',
    '[@media(min-height:721px)_and_(max-height:820px)]:gap-3',
  ].join(' ');

  const eyebrowClassName = [
    'max-w-4xl font-medium uppercase tracking-widest text-muted-foreground',
    'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl',
    '2xl:text-2xl min-[1920px]:text-3xl min-[2560px]:text-4xl',
    // Mutually exclusive short-viewport caps.
    '[@media(max-height:720px)_and_(max-width:479px)]:text-[10px]',
    '[@media(max-height:720px)_and_(min-width:480px)]:text-xs',
    '[@media(min-height:721px)_and_(max-height:820px)]:text-sm',
  ].join(' ');

  return (
    <div
      className={cn(wrapperGapClassName, 'items-start text-left', className)}
    >
      {eyebrow ? (
        <p
          className={eyebrowClassName}
          style={accentColor ? { color: accentColor } : undefined}
        >
          {eyebrow}
        </p>
      ) : null}
      {testimonial ? (
        <blockquote
          className={titleClassName}
          style={titleStyle}
          cite={bodyHref}
        >
          {title}
        </blockquote>
      ) : (
        <div className={titleClassName} style={titleStyle}>
          {title}
        </div>
      )}
      {body ? (
        bodyHref ? (
          <a
            href={bodyHref}
            target="_blank"
            rel="noopener noreferrer"
            className={joinClassNames(
              bodyLinkClassName,
              accentColor ? undefined : 'text-muted-foreground'
            )}
            style={bodyColorStyle}
          >
            {body}
          </a>
        ) : (
          <p
            className={bodyParagraphClassName}
            style={accentColor ? { color: accentColor } : undefined}
          >
            {body}
          </p>
        )
      ) : null}
    </div>
  );
};

export default HeroTextPanel;
